import { ApolloClient, InMemoryCache, HttpLink, from, ApolloLink, fromPromise } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { AuthTokens } from '../../utils/AuthApi'

interface GQLClientProps {
  getAuthTokens: () => AuthTokens
  refreshAuthTokens: () => Promise<void>
  GQLApiEnvironment: string
}

function makeGQLClient({ getAuthTokens, refreshAuthTokens, GQLApiEnvironment }: GQLClientProps) {
  // get csrf before each request
  const authLink = new ApolloLink((operation, forward) => {
    const csrfToken = getAuthTokens().csrfToken
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {}) },
    }))
    return forward(operation)
  })

  const httpLink = new HttpLink({
    uri: GQLApiEnvironment,
    credentials: 'include',
  })

  const errorLink = onError(({ networkError, operation, forward }) => {
    // 418 = csrf token invalid
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 418) {
      const refreshAuthTokensPromise = refreshAuthTokens()
      refreshAuthTokensPromise.then(() => {
        operation.setContext(({ headers = {} }) => {
          const token = getAuthTokens().csrfToken
          return {
            headers: { ...headers, ...(token ? { 'X-XSRF-TOKEN': token } : {}) },
          }
        })
      })
      return fromPromise(refreshAuthTokensPromise).flatMap(() => forward(operation))
    } else if (networkError) {
      console.log(networkError)
    }
  })

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authLink, errorLink, httpLink]),
  })

  return client
}

export default makeGQLClient
